import {
  GraphComponent,
  GraphItemTypes,
  GraphViewerInputMode,
  Point,
  StyleIndicatorZoomPolicy,
  TimeSpan,
  NodeStyleIndicatorRenderer,
  EdgeStyleIndicatorRenderer,
  MouseWheelBehaviors,
} from '@ardoq/yfiles';
import { initializeYFilesLicense } from '../../../transpiledLibs/yfiles/license/license';
import { installHoverMode } from 'yfilesExtensions/view/modernized/highlightControls';
import { NodeSelectionStyle } from 'yfilesExtensions/styles/NodeSelectionStyle';
import { EdgeSelectionStyle } from 'yfilesExtensions/styles/EdgeSelectionStyle';
import { HoverModeArgs } from 'yfilesExtensions/view/modernized/types';
import { modernizedTooltipListener } from './modernizedTooltipListener';
import { type SetUrlFieldValuesPopover } from './getUrlFieldValuesPopoverState';
import {
  MODERNIZED_BLOCK_DIAGRAM_SELECTION_STROKE_WIDTH as SELECTION_STROKE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_NODE_BORDER_WIDTH as NODE_BORDER_WIDTH,
} from 'yfilesExtensions/styles/modernized/consts';

initializeYFilesLicense();

const createInputMode = (
  graphComponent: GraphComponent,
  hoverArgs: HoverModeArgs,
  setUrlFieldValuesPopover: SetUrlFieldValuesPopover
) => {
  const result = new GraphViewerInputMode({
    itemHoverInputMode: installHoverMode(graphComponent, hoverArgs),
    toolTipItems:
      GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL,
    clickableItems:
      GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL,
    clickHitTestOrder: [
      GraphItemTypes.EDGE_LABEL,
      GraphItemTypes.EDGE,
      GraphItemTypes.NODE_LABEL,
      GraphItemTypes.NODE,
    ],
  });
  const [destroyPopover, tooltipListener] = modernizedTooltipListener(
    setUrlFieldValuesPopover
  );
  result.addEventListener('query-item-tool-tip', tooltipListener);
  result.toolTipInputMode.addEventListener('query-tool-tip', e => {
    if (!e.handled) {
      destroyPopover();
    }
  });
  result.toolTipInputMode.toolTipLocationOffset = new Point(10, 10);
  result.toolTipInputMode.duration = TimeSpan.fromMilliseconds(999999); // continue showing the tooltip "forever" or until the user moves the mouse away
  result.toolTipInputMode.delay = TimeSpan.fromMilliseconds(200); // small delay to prevent drag interference
  return result;
};

export const applySelectionDecorators = (
  graphComponent: GraphComponent,
  nodeMargin = 25,
  edgeThickness = 10,
  isModern = false
) => {
  const graphDecorator = graphComponent.graph.decorator;
  graphDecorator.nodes.selectionRenderer.addFactory(
    () =>
      new NodeStyleIndicatorRenderer({
        nodeStyle: new NodeSelectionStyle(isModern),
        margins: isModern
          ? NODE_BORDER_WIDTH / 2 + SELECTION_STROKE_WIDTH / 2
          : nodeMargin,
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
      })
  );

  graphDecorator.edges.selectionRenderer.addFactory(
    () =>
      new EdgeStyleIndicatorRenderer({
        edgeStyle: new EdgeSelectionStyle(edgeThickness),
        zoomPolicy: StyleIndicatorZoomPolicy.WORLD_COORDINATES,
      })
  );
};

export const createGraphComponent = (
  container: HTMLDivElement,
  hoverArgs: HoverModeArgs,
  setUrlFieldValuesPopover: SetUrlFieldValuesPopover
) => {
  const graphComponent = new GraphComponent(container);
  graphComponent.inputMode = createInputMode(
    graphComponent,
    hoverArgs,
    setUrlFieldValuesPopover
  );
  graphComponent.minimumZoom = 0.1;
  graphComponent.mouseWheelBehavior = MouseWheelBehaviors.ZOOM; // disable wheel-panning because it can cause confusion for trackpad users who are trying to zoom with a two-finger swipe.
  return graphComponent;
};

export const selectableItems: GraphItemTypes =
  GraphItemTypes.NODE | GraphItemTypes.EDGE;
export const focusableItems = GraphItemTypes.NONE;
